import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import Modal from '../../Components/Modal/Modal';
import Icon, { IconType } from '../../Components/Icon/Icon';
import StripPayment, { istripForm } from '../../Components/Organization/stripePayment';
import { sanitizeHtml } from '../../utils/Tools';
import redux from '../../redux/redux';
import { FormatDate } from '../../utils/Tools';
import HousingPackageFormFields from '../../Components/Store/HousingPackageFormFields';

const REGISTRATION_PRODUCT_ID = -100;
const DONATION_PRODUCT_ID = -500;
const ATTENDEE_FEE_PRODUCT_ID = -200;

interface IOrderItem {
  OrderedItemId: number;
  OrderableItemId: number;
  Name: string;
  Custom: string | null;
  Quantity: number;
  CostInCents: number;
  Refunded: boolean;
  Size: string | null;
  AdvancedType: string | null;
  PublicId: string;
  RemovedAtUtc: string | null;
}

interface IAccountHolder {
  FirstName: string;
  LastName: string;
  Address1: string;
  City: string;
  State: string;
  Zip: string;
  Country: string | null;
}

interface IInvoiceAddress {
  Name: string;
  Address1: string;
  Address2: string | null;
  City: string;
  State: string;
  Zip: string;
  Country: string | null;
}

interface ICoupon {
  Id: number;
  Code: string;
}

interface IPurchaseInvoiceData {
  OrderId: number;
  PublicId: string;
  OrderAtUtc: string;
  TotalInCents: number;
  TotalAmountPaid: number;
  CardProcessingFeeInCents: number | null;
  Paid: boolean;
  OrderStatus: string;
  PaymentDate: string | null;
  PaidOnUtc: string | null;
  RefundedAtUtc: string | null;
  RemovedAtUtc: string | null;
  TransactionNumber: string | null;
  PaymentNotes: string | null;
  BillTo: string | null;
  Memo: string | null;
  Email: string | null;
  ClientPaid: string | null;
  PersonId: number;
  OrderItems: IOrderItem[];
  AccountHolder: IAccountHolder;
  FairName: string;
  FairContactName: string;
  FairAddress1: string;
  FairAddress2: string | null;
  FairCity: string;
  FairState: string;
  FairZip: string;
  SiteUrl: string;
  InvoiceAddress: IInvoiceAddress | null;
  AffiliateLocationCode: string | null;
  Coupons: ICoupon[];
  CouponSavingsInCents: number;
  CouponSavingsByCoupon: Record<string, number> | null;
  TermsAndConditions: string | null;
  QrCode: string;
  FeeInCents: number;
  TeamInvoices: number[];
  IsStaff: boolean;
  IsOwner: boolean;
  IsSystemOwner: boolean;
  ChargeBackOn: string | null;
  RefundsEnabled: boolean;
  UsingStripe: boolean;
  AcceptCreditDebitCards: boolean;
  ChargeProcessingFees: boolean;
  InvoiceShowBillToSection: boolean;
  StripePublicKey: string | null;
}

const PurchaseInvoicePage = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [info, setInfo] = useState<IPurchaseInvoiceData | null>(null);
  const [showPayNow, setShowPayNow] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showBillTo, setShowBillTo] = useState(false);
  const [showPartialRefund, setShowPartialRefund] = useState(false);
  const [showingRefundItems, setShowingRefundItems] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [recordPaymentNotes, setRecordPaymentNotes] = useState('');
  const [billToText, setBillToText] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [partialRefundAmount, setPartialRefundAmount] = useState('');
  const [partialRefundNotes, setPartialRefundNotes] = useState('');
  const [editingHousingItem, setEditingHousingItem] = useState<IOrderItem | null>(null);
  const [housingFormData, setHousingFormData] = useState<Record<string, string>>({});

  useEffect(() => {
    if (invoiceId) loadData();
  }, [invoiceId]);

  const loadData = async () => {
    const res = await store.server.getApi2<IPurchaseInvoiceData>(`../Purchase/GetInvoiceData/${invoiceId}`);
    if (res.Success) {
      setInfo(res.Value);
      setBillToText(res.Value.BillTo ?? '');
      if (res.Value.StripePublicKey) {
        setStripePromise(loadStripe(res.Value.StripePublicKey));
      }
    } else {
      toast.error(res.Message ?? 'Failed to load invoice', { autoClose: false });
    }
  };

  const callAndReload = async (url: string, method: 'GET' | 'POST' = 'GET', body?: object) => {
    redux.loading(true);
    try {
      await fetch(url, {
        method,
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch {
      // ignore, reload to reflect current state
    }
    await loadData();
  };

  const removeInvoice = () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    callAndReload(`/Purchase/RemoveInvoice/${info!.PublicId}`);
  };

  const refundInvoice = () => {
    const amount = info!.TotalAmountPaid.toFixed(2);
    let msg: string;
    if (info!.UsingStripe && info!.TransactionNumber) {
      msg = `Are you sure you want to refund this order for $ ${amount}? Please note that this money will be placed back on the card that was charged.`;
    } else if (info!.TransactionNumber) {
      msg = `Are you sure you want to refund this order for $ ${amount}? Please note that the refund must still be made in the merchant account.`;
    } else {
      msg = `Are you sure you want to refund this order for $ ${amount}?`;
    }
    if (!window.confirm(msg)) return;
    callAndReload(`/Purchase/RecordRefundOfInvoice/${info!.PublicId}`);
  };

  const recordPayment = async () => {
    const res = await store.server.postApi<ServerResponse<boolean>>(`../Purchase/RecordPaymentOfInvoice/${info!.PublicId}`, { notes: recordPaymentNotes });
    if (res.Success) {
      setShowRecordPayment(false);
      setRecordPaymentNotes('');
      await loadData();
    } else {
      toast.error(res.Message ?? 'Failed to record payment');
    }
  };

  const removeLineItem = (itemId: number, orderId: string) => {
    const confirmed =
      info!.Paid && info!.UsingStripe
        ? 'Are you sure you want to remove this line item and refund it? This cannot be undone.'
        : 'Are you sure you want to remove this line item? This cannot be undone.';
    if (!window.confirm(confirmed)) return;
    callAndReload(`/Purchase/RemoveItem/${itemId}?orderId=${orderId}`);
  };

  const saveBillTo = async () => {
    const res = await store.server.postApi<ServerResponse<string>>('../Purchase/SetBillTo', {
      text: billToText,
      orderId: info!.OrderId,
    });
    if (res.Success) {
      setShowBillTo(false);
      await loadData();
    } else {
      toast.error(res.Message ?? 'Failed to update');
    }
  };

  const doPartialRefund = async () => {
    const amount = parseFloat(partialRefundAmount);
    if (!amount || amount <= 0) {
      alert('Please enter a valid refund amount.');
      return;
    }
    const res = await store.server.postApi<ServerResponse<boolean>>('../Purchase/PartialRefund', {
      orderId: info!.PublicId,
      refundAmount: amount,
      notes: partialRefundNotes,
    });
    if (res.Success) {
      setShowPartialRefund(false);
      setPartialRefundAmount('');
      setPartialRefundNotes('');
      await loadData();
    } else {
      toast.error(res.Message ?? 'Failed to process refund');
    }
  };

  const addCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code.');
      return;
    }
    const res = await store.server.postApi<ServerResponse<boolean>>('../store/AddCoupon2', {
      code: couponCode,
      orderId: info!.PublicId,
    });
    if (res.Success) {
      setCouponCode('');
      await loadData();
    } else {
      toast.error(res.Message ?? 'Failed to apply coupon');
    }
  };

  const removeCoupon = async (code: string) => {
    if (!window.confirm(`Are you sure you want to remove the "${code}" coupon?`)) return;
    const res = await store.server.postApi<ServerResponse<boolean>>('../store/RemoveCoupon', {
      code,
      orderId: info!.PublicId,
    });
    if (res.Success) {
      await loadData();
    } else {
      toast.error(res.Message ?? 'Failed to remove coupon');
    }
  };

  const mergeInvoices = async () => {
    if (!window.confirm('Are you sure you want to merge these invoices?')) return;
    const res = await store.server.postApi<ServerResponse<number>>('../purchase/MergeInvoices', {
      id: info!.OrderId,
      idString: info!.TeamInvoices.join(','),
    });
    if (res.Success) {
      await loadData();
    } else {
      toast.error(res.Message ?? 'Failed to merge invoices');
    }
  };

  const recordChargeBack = () => {
    if (!window.confirm('Are you sure you want to record a charge back for this invoice? This will mark the order as charged back.')) return;
    callAndReload(`/Purchase/RecordChargeBack/${info!.PublicId}`);
  };

  const undoChargeBack = () => {
    if (!window.confirm('Are you sure you want to undo the charge back on this invoice? This will mark the order as complete again.')) return;
    callAndReload(`/Purchase/UndoChargeBack/${info!.PublicId}`);
  };

  const sponsorshipPublished = (item: IOrderItem) => {
    try {
      const d = item.Custom ? JSON.parse(item.Custom) : {};
      return !!d.SponsorId;
    } catch {
      return false;
    }
  };

  const publishSponsorship = async (item: IOrderItem) => {
    const res = await store.server.postApi<ServerResponse<{ sponsorId: number; name: string }>>('../Sponsors/PublishFromOrder', {
      orderedItemId: item.OrderedItemId,
    });
    if (res.Success) {
      toast.success(`Published ${res.Value?.name || 'sponsor'}.`);
      await loadData();
    } else {
      toast.error(res.Message ?? 'Could not publish sponsor.', { autoClose: false });
    }
  };

  const openEditHousing = (item: IOrderItem) => {
    const existing = item.Custom ? JSON.parse(item.Custom) : {};
    setHousingFormData(existing);
    setEditingHousingItem(item);
  };

  const saveHousingDetails = async () => {
    const res = await store.server.postApi<ServerResponse<boolean>>('../Purchase/UpdateHousingDetails', {
      orderedItemId: editingHousingItem!.OrderedItemId,
      custom: JSON.stringify(housingFormData),
    });
    if (res.Success) {
      setEditingHousingItem(null);
      setHousingFormData({});
      await loadData();
    } else {
      toast.error(res.Message ?? 'Failed to update housing details');
    }
  };

  const handleStripePayNow = async (stripe: Stripe, elements: StripeElements, form: istripForm) => {
    const errors: string[] = [];
    if (!form.firstName) errors.push('First name is required.');
    if (!form.lastName) errors.push('Last name is required.');
    if (!form.email) errors.push('Email is required.');
    if (!form.phone) errors.push('Phone is required.');
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    const cardElement = elements.getElement('card');
    if (!cardElement) return;

    const result = await stripe.createToken(cardElement);
    if (result.error) {
      alert(result.error.message);
      return;
    }

    const token = result.token;
    const request = {
      FirstName: form.firstName,
      LastName: form.lastName,
      Email: form.email,
      Phone: form.phone,
      Last4: token.card?.last4,
      Year: token.card?.exp_year,
      Month: token.card?.exp_month,
      Zip: token.card?.address_zip,
      OrderId: info!.PublicId,
      Token: token.id,
      Notes: '',
    };

    const res = await store.server.postApi<string>('../Purchase/ProcessPayment', request);
    if (typeof res === 'string' && res.toLowerCase() === 'paid') {
      alert('This invoice was already paid!');
    } else if (typeof res === 'string' && res.toLowerCase().startsWith('error')) {
      alert('Sorry we cannot process your payment at this time.\n' + res.split('|')[1]);
      return;
    }
    setShowPayNow(false);
    await loadData();
  };

  const getItemName = (item: IOrderItem): string => {
    if (item.OrderableItemId === REGISTRATION_PRODUCT_ID) return 'Registration Fee';
    if (item.OrderableItemId === DONATION_PRODUCT_ID) return 'Donation';
    if (item.OrderableItemId === ATTENDEE_FEE_PRODUCT_ID) return 'Attendee Fee';
    return item.Name;
  };

  const getLineTotal = (item: IOrderItem): number => (item.CostInCents * item.Quantity) / 100;

  const formatCents = (cents: number): string => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const renderItemDetails = (item: IOrderItem) => {
    try {
      const custom = item.Custom ?? '';
      if (item.AdvancedType === '7000') {
        const d = JSON.parse(custom);
        return (
          <div className="text-muted">
            For: {d.OccupantsFirstName} {d.OccupantsLastName}
            <br />
            Age Range: {d.OccupantsAge}
            <br />
            Roommate: {d.RoommatesName}
            <br />
            Chaperone: {d.ChaperonesName}
          </div>
        );
      }
      if (item.AdvancedType === '8000') {
        const d = JSON.parse(custom);
        return (
          <div className="text-muted">
            Sender: {d.SendersName}
            <br />
            Return Address:
            <br />
            {d.AddressLine1}
            <br />
            {d.AddressLine2}
            <br />
            {d.AddressLine3}
            <br />
            {d.AddressLine4}
          </div>
        );
      }
      if (item.AdvancedType === '7500' || item.OrderableItemId === ATTENDEE_FEE_PRODUCT_ID) {
        const d = JSON.parse(custom);
        if (d?.FirstName) {
          return (
            <div className="text-muted">
              {d.Relationship}
              <br />
              {d.FirstName} {d.LastName}
              <br />
              {d.AgeRange}
            </div>
          );
        }
        if (d?.Name) return <div className="text-muted">{d.Name}</div>;
        if (custom) return <div className="text-muted" dangerouslySetInnerHTML={{ __html: sanitizeHtml(custom) }} />;
      }
      if (item.AdvancedType === '3000') {
        const d = JSON.parse(custom);
        if (d) {
          return (
            <div>
              {d.FirstName} {d.LastName}
              {d.Email && (
                <>
                  <br />
                  {d.Email}
                </>
              )}
              {d.Phone && (
                <>
                  <br />
                  {d.Phone}
                </>
              )}
            </div>
          );
        }
      }
      if (item.AdvancedType === '14000') {
        const d = JSON.parse(custom);
        if (d) {
          return (
            <div className="text-muted">
              {d.OrgName}
              {d.Website && (
                <>
                  <br />
                  {d.Website}
                </>
              )}
              {d.ContactName && (
                <>
                  <br />
                  {d.ContactName}
                </>
              )}
              {d.Email && (
                <>
                  <br />
                  {d.Email}
                </>
              )}
              {d.SponsorId ? (
                <>
                  <br />
                  Published as sponsor
                </>
              ) : null}
            </div>
          );
        }
      }
    } catch {
      // fall through to default
    }

    return (
      <>
        {item.Size && <span>{item.Size} </span>}
        {item.Custom && <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.Custom) }} />}
      </>
    );
  };

  if (!info) return <div>Loading...</div>;

  const isPending = !info.Paid && !info.RemovedAtUtc && info.OrderStatus !== 'Refunded';
  const totalPaid = info.TotalAmountPaid + (info.CardProcessingFeeInCents ?? 0) / 100;
  const totalWithFee = info.TotalInCents + info.FeeInCents;
  const canShowPayNow = isPending && info.AcceptCreditDebitCards && info.UsingStripe;

  return (
    <div className="purchase-invoice-page">
      {/* Action buttons (no-print) */}

      {/* Invoice panel */}
      <div className="panel panel-default">
        <div className="panel-heading no-print">
          <div className="flex-between">
            <div>
              <Icon type={IconType.invoice} /> &nbsp;Invoice
            </div>
            <div className='no-print'>
              {isPending && (
                <div className="no-print btn-group">
                  {canShowPayNow && (
                    <button type="button" className="btn btn-success" onClick={() => setShowPayNow(true)}>
                      <Icon type={IconType.creditCard} />
                      &nbsp;Pay Now
                    </button>
                  )}
                  {info.IsStaff && (
                    <button type="button" className="btn btn-primary" onClick={() => setShowRecordPayment(true)}>
                      <Icon type={IconType.save} />
                      &nbsp;Record Payment
                    </button>
                  )}
                  {info.RefundsEnabled && (
                    <>
                      <button type="button" className="btn btn-default" onClick={() => setShowingRefundItems((x) => !x)}>
                        <Icon type={IconType.edit} />
                        &nbsp;Refund/Remove Item
                      </button>
                      <button type="button" className="btn btn-default" onClick={() => setShowPartialRefund(true)}>
                        <Icon type={IconType.edit} />
                        &nbsp;Do Partial Refund
                      </button>
                    </>
                  )}
                  {(info.IsStaff || info.IsOwner) && (
                    <button type="button" className="btn btn-warning" onClick={removeInvoice}>
                      <Icon type={IconType.delete} />
                      &nbsp;Delete Invoice
                    </button>
                  )}
                </div>
              )}

              <div className="no-print btn-group">
                {info.Paid && info.IsStaff && info.OrderStatus === 'Complete' && info.RefundsEnabled && (
                  <>
                    <button type="button" className="btn btn-warning refund-remove" onClick={refundInvoice}>
                      <Icon type={IconType.undo} />
                      &nbsp;Refund Payment
                    </button>
                    <button type="button" className="btn btn-default" onClick={() => setShowingRefundItems((x) => !x)}>
                      <Icon type={IconType.edit} />
                      &nbsp;Refund/Remove Item
                    </button>
                    <button type="button" className="btn btn-default" onClick={() => setShowPartialRefund(true)}>
                      <Icon type={IconType.edit} />
                      &nbsp;Do Partial Refund
                    </button>
                  </>
                )}

                {info.IsSystemOwner && info.Paid && info.OrderStatus === 'Complete' && (
                  <button type="button" className="btn btn-danger" onClick={recordChargeBack}>
                    <i className="fa fa-exclamation-triangle"></i>&nbsp;Record Charge Back
                  </button>
                )}
                {info.IsSystemOwner && info.OrderStatus === 'Charge Back' && (
                  <button type="button" className="btn btn-default" onClick={undoChargeBack}>
                    <Icon type={IconType.undo} />
                    &nbsp;Undo Charge Back
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="panel-body">
          <table width="100%">
            <tbody>
              {/* Header row */}
              <tr>
                <td>
                  <h2>{info.FairName}</h2>
                </td>
                <td className="text-right">
                  <div className="invoice-info">
                    <div>
                      <h3>Invoice: {info.OrderId}</h3>
                      {FormatDate(info.OrderAtUtc, 'pretty')}
                      <h4>{info.PublicId}</h4>
                      <br />
                      <br />
                    </div>
                    <div>
                      <img className="qr-code" src={info.QrCode} alt="QR Code" />
                    </div>
                  </div>
                </td>
              </tr>

              {/* Address row */}
              <tr>
                <td>
                  {/* Bill To */}
                  {info.InvoiceShowBillToSection && (
                    <div>
                      <strong>Bill To:</strong>
                      <br />
                      {info.IsStaff && !info.Paid && info.OrderStatus !== 'Refunded' && (
                        <div className="no-print">
                          <button type="button" className="btn btn-xs btn-default" onClick={() => setShowBillTo(true)}>
                            <i className="fa fa-edit"></i> Customize Bill To
                          </button>
                        </div>
                      )}
                      {info.BillTo ? (
                        <div className="bill-to">{info.BillTo}</div>
                      ) : (
                        <div>
                          {(info.AccountHolder.FirstName || info.AccountHolder.LastName) && (
                            <>
                              {info.AccountHolder.FirstName} {info.AccountHolder.LastName}
                              <br />
                            </>
                          )}
                          {info.AccountHolder.Address1 && (
                            <>
                              {info.AccountHolder.Address1}
                              <br />
                            </>
                          )}
                          {(info.AccountHolder.City || info.AccountHolder.State || info.AccountHolder.Zip) && (
                            <>
                              {[info.AccountHolder.City, info.AccountHolder.State, info.AccountHolder.Zip].filter(Boolean).join(', ')}
                              <br />
                            </>
                          )}
                          {info.AffiliateLocationCode && (
                            <>
                              <span>Affiliate: {info.AffiliateLocationCode}</span>
                              <br />
                            </>
                          )}
                          {info.Email && (
                            <>
                              {info.Email}
                              <br />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Memo */}
                  {info.Memo && (
                    <div>
                      Memo:
                      <br />
                      <strong>{info.Memo}</strong>
                    </div>
                  )}
                </td>
                {/* Fair / Invoice address */}
                <td className="text-right">
                  {info.InvoiceAddress ? (
                    <>
                      <strong>Attn: {info.InvoiceAddress.Name}</strong>
                      <br />
                      {info.InvoiceAddress.Address1}
                      <br />
                      {info.InvoiceAddress.Address2 && (
                        <>
                          {info.InvoiceAddress.Address2}
                          <br />
                        </>
                      )}
                      {info.InvoiceAddress.City}, {info.InvoiceAddress.State}, {info.InvoiceAddress.Zip}
                      <br />
                      {info.InvoiceAddress.Country && (
                        <>
                          <span>{info.InvoiceAddress.Country}</span>
                          <br />
                        </>
                      )}
                      <br />
                      {info.SiteUrl}
                      <br />
                    </>
                  ) : (
                    <>
                      <strong>Attn: {info.FairContactName}</strong>
                      <br />
                      {info.FairAddress1}
                      <br />
                      {info.FairAddress2 && (
                        <>
                          {info.FairAddress2}
                          <br />
                        </>
                      )}
                      {info.FairCity}, {info.FairState}, {info.FairZip}
                      <br />
                      <br />
                      {info.SiteUrl}
                      <br />
                    </>
                  )}
                </td>
              </tr>

              {/* Status banners */}
              <tr>
                <td colSpan={2}>
                  <br />
                  {info.RemovedAtUtc ? (
                    <div className="alert alert-warning">
                      <h4>
                        <i className="fa fa-check-square-o"></i>&nbsp;Deleted
                      </h4>
                      This invoice/PO has been removed and is no longer valid.
                      {info.TransactionNumber && (
                        <>
                          <div>Invoice: {info.OrderId}</div>
                          <div>Transaction: {info.TransactionNumber}</div>
                        </>
                      )}
                    </div>
                  ) : info.Paid && info.OrderStatus === 'Complete' ? (
                    <div className="alert alert-success">
                      <h4>
                        <i className="fa fa-check-circle"></i> Paid
                      </h4>
                      {info.PaidOnUtc && <div>{FormatDate(info.PaidOnUtc, 'pretty')}</div>}
                      This invoice/PO has been paid in full.
                      {info.TransactionNumber && <span> Credit Card Confirmation Number: {info.TransactionNumber}</span>}
                    </div>
                  ) : !info.Paid && info.OrderStatus === 'Refunded' ? (
                    <div className="alert alert-warning">
                      <h4>
                        <i className="fa fa-undo"></i>&nbsp;Refunded
                      </h4>
                      {info.RefundedAtUtc && (
                        <span>
                          {FormatDate(info.RefundedAtUtc, 'pretty')}
                          <br />
                        </span>
                      )}
                      This invoice/PO has been refunded.
                      {info.TransactionNumber && (
                        <>
                          <div>Invoice: {info.OrderId}</div>
                          <div>Transaction: {info.TransactionNumber}</div>
                        </>
                      )}
                    </div>
                  ) : null}

                  {info.OrderStatus === 'Charge Back' && (
                    <div className="alert alert-danger">
                      <h2>
                        <i className="fa fa-exclamation-triangle"></i>&nbsp;Charge Back!
                      </h2>
                      {info.ChargeBackOn && <p>{FormatDate(info.ChargeBackOn, 'pretty')}</p>}
                      <p>The card holder issued a charge back on this invoice. Please contact the card holder to resolve this issue.</p>
                      {info.TransactionNumber && <p>Card Transaction: {info.TransactionNumber}</p>}
                    </div>
                  )}

                  {info.PaymentNotes && (
                    <div className="payment-note alert alert-info">
                      <h4>
                        <i className="fa-solid fa-memo-pad"></i> Note
                      </h4>
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(info.PaymentNotes.replace(/\n/g, '<br/>')) }} />
                    </div>
                  )}
                  <br />
                </td>
              </tr>

              {/* Line items table */}
              <tr>
                <td colSpan={2}>
                  <table className="table table-striped table-hover" width="100%">
                    <thead>
                      <tr>
                        <th colSpan={2}>Product</th>
                        <th>Quantity</th>
                        <th className="text-right">Cost</th>
                        <th className="text-right">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {info.OrderItems.map((item, i) => (
                        <tr key={`item-${i}`} style={item.Refunded ? { textDecoration: 'line-through' } : undefined}>
                          <td style={{ width: 35 }}>
                            {!item.Refunded && info.RefundsEnabled && showingRefundItems && (
                              <span
                                className="text-danger"
                                title="Refund/Remove this item from this order."
                                style={{ cursor: 'pointer' }}
                                onClick={() => removeLineItem(item.OrderedItemId, item.PublicId)}>
                                &nbsp;&nbsp;<i className="fa fa-trash"></i>&nbsp;&nbsp;
                              </span>
                            )}
                          </td>
                          <td>
                            {getItemName(item)}
                            {renderItemDetails(item)}
                            {info.IsStaff && item.AdvancedType === '7000' && (
                              <div className="no-print">
                                <button type="button" className="btn btn-xs btn-default" onClick={() => openEditHousing(item)}>
                                  <Icon type={IconType.edit} /> Edit Details
                                </button>
                              </div>
                            )}
                            {info.IsStaff && item.AdvancedType === '14000' && !sponsorshipPublished(item) && (
                              <div className="no-print">
                                <button type="button" className="btn btn-xs btn-default" onClick={() => publishSponsorship(item)}>
                                  <Icon type={IconType.check} /> Publish as Sponsor
                                </button>
                              </div>
                            )}
                          </td>
                          <td>{item.Quantity}</td>
                          <td className="text-right">$&nbsp;{formatCents(item.CostInCents)}</td>
                          <td className="text-right">$&nbsp;{getLineTotal(item).toFixed(2)}</td>
                        </tr>
                      ))}

                      {/* Coupons */}
                      {info.Coupons.length > 0 && info.CouponSavingsByCoupon && Object.keys(info.CouponSavingsByCoupon).length > 0 && (
                        <>
                          <tr>
                            <td colSpan={2}>
                              <strong>Coupons</strong>
                            </td>
                            <td></td>
                            <td></td>
                            <td></td>
                          </tr>
                          {info.Coupons.map((coupon, i) => {
                            const savings = info.CouponSavingsByCoupon?.[coupon.Id.toString()];
                            if (savings === undefined) {
                              return (
                                <tr key={`coupon-${i}`}>
                                  <td></td>
                                  <td>{coupon.Code}</td>
                                  <td></td>
                                  <td className="text-right"></td>
                                  <td className="text-right"></td>
                                </tr>
                              );
                            }
                            return (
                              <tr key={`coupon-${i}`}>
                                <td>
                                  {isPending && (
                                    <span className="click no-print" style={{ cursor: 'pointer' }} onClick={() => removeCoupon(coupon.Code)}>
                                      <i className="fa fa-trash"></i>
                                    </span>
                                  )}
                                </td>
                                <td>{coupon.Code}</td>
                                <td>{formatCents(savings)}</td>
                                <td className="text-right">Savings</td>
                                <td className="text-right">$ {formatCents(savings)}</td>
                              </tr>
                            );
                          })}
                          <tr>
                            <td>&nbsp;</td>
                            <td></td>
                            <td></td>
                            <td className="text-right">Total Savings</td>
                            <td className="text-right">$ {formatCents(info.CouponSavingsInCents)}</td>
                          </tr>
                          <tr>
                            <td colSpan={5}>&nbsp;</td>
                          </tr>
                        </>
                      )}

                      {/* Processing fee (if paid with card) */}
                      {info.Paid && (info.CardProcessingFeeInCents ?? 0) > 0 && (
                        <tr>
                          <td style={{ width: 35 }}></td>
                          <td></td>
                          <td></td>
                          <td className="text-right">
                            <strong>Processing Fee:</strong>
                          </td>
                          <td className="text-right">
                            <strong>$&nbsp;{formatCents(info.CardProcessingFeeInCents ?? 0)}</strong>
                          </td>
                        </tr>
                      )}

                      {/* Total */}
                      <tr style={{ fontSize: '1.3em' }}>
                        <td style={{ width: 35 }}></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">
                          <strong>Total:</strong>
                        </td>
                        <td className="text-right">
                          <strong>
                            $&nbsp;
                            {totalPaid.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Pending actions */}
                  {isPending && (
                    <div className="no-print">
                      {info.TeamInvoices.length > 0 && (
                        <div className="alert alert-info">
                          <h2>
                            <i className="fa fa-info-circle"></i>&nbsp;Team Invoices Found
                          </h2>
                          <p>We have detected multiple open team invoices, would you like to combine these invoices into one? Each invoice contains a registration fee.</p>
                          <ul>
                            {info.TeamInvoices.map((id) => (
                              <li key={`ti-${id}`}>Invoice: {id}</li>
                            ))}
                          </ul>
                          <button type="button" className="btn btn-info" onClick={mergeInvoices}>
                            <i className="fad fa-merge"></i> Combine Invoices
                          </button>
                        </div>
                      )}

                      {canShowPayNow && (
                        <div className="form-group text-right">
                          <button type="button" className="btn btn-success" onClick={() => setShowPayNow(true)}>
                            <Icon type={IconType.creditCard} />
                            &nbsp;Pay Now
                          </button>
                        </div>
                      )}

                      {/* Coupon input */}
                      <div className="coupon-info">
                        <div className="form-group">
                          <label htmlFor="new-coupon" className="control-label">
                            Coupon
                          </label>
                          <div>
                            <input type="text" maxLength={100} className="form-control" placeholder="coupon" id="new-coupon" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} />
                          </div>
                          <div>
                            <br />
                            <button type="button" className="btn btn-secondary" onClick={addCoupon}>
                              Apply Coupon
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {info.TermsAndConditions && <div className="terms-conditions" dangerouslySetInnerHTML={{ __html: sanitizeHtml(info.TermsAndConditions) }} />}
          <div>
            <span className="label label-info">
              <strong>You must send a copy of this Invoice with your payment.</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Pay Now Modal */}
      {showPayNow && info && stripePromise && (
        <Modal setModalOpen={setShowPayNow} title="Pay Now" size="l">
          <Elements stripe={stripePromise}>
            <StripPayment Amount={info.TotalInCents / 100} handlePayNow={handleStripePayNow} feeAmount={info.FeeInCents / 100} feePercent={0} includeFees={info.ChargeProcessingFees} />
          </Elements>
        </Modal>
      )}

      {/* Record Payment Modal */}
      {showRecordPayment && (
        <Modal setModalOpen={setShowRecordPayment} title="Record Payment" size="l">
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="record-payment-note">
                Note:
              </label>
              <div className="col-sm-8">
                <textarea className="form-control" id="record-payment-note" placeholder="Payment notes" rows={3} value={recordPaymentNotes} onChange={(e) => setRecordPaymentNotes(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <button type="button" className="btn btn-primary" onClick={recordPayment}>
                  <Icon type={IconType.save} />
                  &nbsp;Record Payment
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Bill To Modal */}
      {showBillTo && (
        <Modal setModalOpen={setShowBillTo} title="Edit Bill To" size="l">
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="bill-to-text">
                Bill To:
              </label>
              <div className="col-sm-8">
                <textarea id="bill-to-text" rows={5} maxLength={1000} className="form-control" value={billToText} onChange={(e) => setBillToText(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <button type="button" className="btn btn-primary" onClick={saveBillTo}>
                  <Icon type={IconType.save} />
                  &nbsp;Save
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Partial Refund Modal */}
      {showPartialRefund && (
        <Modal setModalOpen={setShowPartialRefund} title="Partial Refund" size="l">
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="partial-refund-amount">
                Amount to Refund:
              </label>
              <div className="col-sm-8">
                <input
                  type="number"
                  className="form-control"
                  id="partial-refund-amount"
                  placeholder="Refund Amount"
                  value={partialRefundAmount}
                  onChange={(e) => setPartialRefundAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="partial-refund-note">
                Note:
              </label>
              <div className="col-sm-8">
                <textarea className="form-control" id="partial-refund-note" placeholder="Notes" rows={3} value={partialRefundNotes} onChange={(e) => setPartialRefundNotes(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <button type="button" className="btn btn-primary" onClick={doPartialRefund}>
                  <i className="fa-solid fa-people-pulling"></i>&nbsp;Process Partial Refund
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Housing Details Modal */}
      {editingHousingItem && (
        <Modal setModalOpen={() => setEditingHousingItem(null)} title="Edit Housing Package Details" size="l">
          <div className="form-horizontal">
            <HousingPackageFormFields
              formData={housingFormData}
              onChange={(key, value) => setHousingFormData((prev) => ({ ...prev, [key]: value }))}
            />
          </div>
          <div className="form-group">
            <div className="col-sm-6 col-sm-offset-6">
              <button type="button" className="btn btn-primary" onClick={saveHousingDetails}>
                <Icon type={IconType.save} /> Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PurchaseInvoicePage;
